import React, { useEffect, useRef, useState } from 'react';
import { Radar } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';

const SonarPanel = () => {
  const canvasRef = useRef(null);
  const [sonarData, setSonarData] = useState({
    depth: 3840,
    pingRate: 2,
    backscatter: -23,
  });

  const getDepthStatus = (depth) => {
    if (depth < 500) return { color: 'text-emerald-400', label: 'Shallow' };
    if (depth < 2000) return { color: 'text-cyan-400', label: 'Mid-depth' };
    if (depth < 4000) return { color: 'text-amber-400', label: 'Deep' };
    return { color: 'text-destructive', label: 'Abyssal' };
  };

  const getPingRateStatus = (rate) => {
    if (rate >= 1.5 && rate <= 3) return { color: 'text-emerald-400', label: 'Optimal' };
    if (rate >= 1 && rate <= 4) return { color: 'text-amber-400', label: 'Acceptable' };
    return { color: 'text-destructive', label: 'Suboptimal' };
  };

  const getBackscatterStatus = (value) => {
    if (value >= -25 && value <= -15) return { color: 'text-emerald-400', label: 'Strong' };
    if (value >= -30 && value <= -10) return { color: 'text-amber-400', label: 'Moderate' };
    return { color: 'text-destructive', label: 'Weak' };
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    const width = canvas.width = canvas.offsetWidth * 2;
    const height = canvas.height = 440;
    ctx.scale(2, 2);

    const drawSonar = () => {
      ctx.clearRect(0, 0, width / 2, height / 2);

      // Waterfall effect - simulated sonar data
      const palette = [
        '#00111c',
        '#002030',
        '#003544',
        '#004a5a',
        '#006070',
        '#008090',
        '#00a0b0',
        '#37d3ff',
      ];

      const cols = 120;
      const rows = 22;
      const cellWidth = (width / 2) / cols;
      const cellHeight = (height / 2) / rows;

      for (let row = 0; row < rows; row++) {
        for (let col = 0; col < cols; col++) {
          // Generate pseudo-random intensity based on position
          const noise = Math.sin(col * 0.1 + row * 0.3 + Date.now() * 0.001) * 0.5 + 0.5;
          const intensity = Math.floor(noise * palette.length);
          ctx.fillStyle = palette[Math.min(intensity, palette.length - 1)];
          ctx.fillRect(col * cellWidth, row * cellHeight, cellWidth, cellHeight);
        }
      }

      // Grid lines
      ctx.strokeStyle = 'rgba(55, 211, 255, 0.1)';
      ctx.lineWidth = 1;
      for (let i = 0; i <= 4; i++) {
        const y = (i / 4) * (height / 2);
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(width / 2, y);
        ctx.stroke();
      }
    };

    drawSonar();
    const interval = setInterval(drawSonar, 500);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setSonarData(prev => ({
        ...prev,
        depth: 3840 + Math.floor((Math.random() - 0.5) * 10),
        backscatter: -23 + Math.floor((Math.random() - 0.5) * 2),
      }));
    }, 3000);

    return () => clearInterval(interval);
  }, []);

  return (
    <Card className="col-span-12 lg:col-span-6 animate-fade-in" data-testid="sonar-panel">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Radar className="h-4 w-4 text-primary" />
          <span className="text-sm font-medium tracking-wide text-muted-foreground uppercase">Multibeam Sonar</span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="rounded-md overflow-hidden bg-[#00111c] mb-4" data-testid="sonar-canvas-container">
          <canvas ref={canvasRef} className="w-full" style={{ height: '220px' }} data-testid="sonar-canvas" role="img" aria-label={`Multibeam sonar waterfall display showing ocean floor data at depth ${sonarData.depth} meters with ping rate ${sonarData.pingRate} Hz`} />
        </div>
        <div className="grid grid-cols-3 gap-4">
          <div data-testid="sonar-stat-depth">
            <p className="text-xs text-muted-foreground mb-1">Depth Under Keel</p>
            <p className={`text-lg font-medium font-mono ${getDepthStatus(sonarData.depth).color}`}>{sonarData.depth} m</p>
            <p className="text-xs text-muted-foreground mt-1">{getDepthStatus(sonarData.depth).label}</p>
          </div>
          <div data-testid="sonar-stat-ping">
            <p className="text-xs text-muted-foreground mb-1">Ping Rate</p>
            <p className={`text-lg font-medium font-mono ${getPingRateStatus(sonarData.pingRate).color}`}>{sonarData.pingRate} Hz</p>
            <p className="text-xs text-muted-foreground mt-1">{getPingRateStatus(sonarData.pingRate).label} (1.5-3 Hz)</p>
          </div>
          <div data-testid="sonar-stat-backscatter">
            <p className="text-xs text-muted-foreground mb-1">Backscatter dB</p>
            <p className={`text-lg font-medium font-mono ${getBackscatterStatus(sonarData.backscatter).color}`}>{sonarData.backscatter}</p>
            <p className="text-xs text-muted-foreground mt-1">{getBackscatterStatus(sonarData.backscatter).label} (-25 to -15 dB)</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default SonarPanel;
